/**
 * Compliance — an identity on the wrong profile, moved to the right one.
 *
 * THE ERRAND THIS EXISTS FOR, in the founder's own words after finding it on
 * his own record:
 *
 *   "my ID is linked to the callout technician, whereas it should be linked to
 *    the provider/tenant profile that my personal email address is linked to
 *    ... let's ensure that this entire process is something that can be done
 *    for clients from within the Portal. that would be key."
 *
 * It happens the same way every time: somebody completes identity verification
 * while signed in as the wrong one of their own accounts. A Callout technician
 * who later signs up as a tenant. A provider who did KYC on a test login. The
 * ID lands where they were standing, not where it belongs.
 *
 * WHY THIS IS ITS OWN SCREEN AND NOT A BUTTON ON THE DOCUMENTS ONE.
 *
 * Reading somebody's footprint and MOVING their identity are two different
 * disclosures under two different permissions — view_kyc_document reads,
 * reattach_an_identity writes — and each is logged with its own reason. Hanging
 * a write on a screen a desk opens to read would mean one reason standing for
 * both acts, which is exactly the collapse Canon 4.2 refuses.
 *
 * THE HALF THAT HAD TO BE BUILT FIRST, and is easy to miss: until script 268
 * fn_identity_footprint could not SHOW the second profile. It matched a profile
 * by its hash or by its own Connect Code, never by master_identity_id, so a
 * person's other account was invisible however correctly it was linked. On the
 * founder's own footprint that showed as Neucore named as a beneficial owner
 * with no "owns this business" row beside it. A desk cannot correct what the
 * screen will not show it, so the screen came first.
 *
 * NO NAMES ANYWHERE. Prohibition 6, and the practical reason behind it: the
 * desk is handed Connect Codes because that is what the person on the phone can
 * read off their own screen. There is deliberately no way to search for a
 * human by name, here or anywhere.
 */

import { useState } from 'react';
import {
  O, SANS, Card, StatusPill, Notice, PrimaryCTA, QuietButton, Spinner,
  Field, textInputStyle,
} from '../ui';
import {
  identityFootprint, reattachIdentity, FOOTPRINT_KINDS,
} from '../lib/compliance';

// The database refuses anything shorter, on both permissions. Saying so before
// somebody types beats letting Postgres throw their sentence away.
const MIN_REASON = 8;

const PERMISSION = {
  permission_key: 'reattach_an_identity',
  description: 'Move a verified identity onto the profile it belongs to.',
};

const Meta = ({ children }) => (
  <p style={{ margin: '5px 0 0', fontFamily: SANS, fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
    {children}
  </p>
);

export default function IdentityReattach() {
  const [lookup, setLookup] = useState('');
  const [readReason, setReadReason] = useState('');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const [moved, setMoved] = useState(null);

  const readTooShort = readReason.trim().length < MIN_REASON;
  const moveTooShort = moveReason.trim().length < MIN_REASON;

  async function open() {
    setBusy(true); setError(null); setRows(null); setMoved(null); setMoveError(null);
    const { data, error: err } = await identityFootprint({
      lookup: lookup.trim(), reason: readReason.trim(),
    });
    setBusy(false);
    if (err) { setError(err); return; }
    setRows(data || []);
  }

  async function move() {
    setMoving(true); setMoveError(null);
    const { data, error: err } = await reattachIdentity({
      from: from.trim(), to: to.trim(), reason: moveReason.trim(),
    });
    setMoving(false);
    if (err) { setMoveError(err); return; }
    setMoved(data);
    // Read it back rather than patching what is on screen. The footprint is the
    // record; a client-side edit of it would be this screen's opinion of what
    // just happened, which is the thing a desk is here to check.
    const { data: fresh } = await identityFootprint({
      lookup: lookup.trim(), reason: readReason.trim(),
    });
    if (fresh) setRows(fresh);
  }

  // THE PROFILES THIS PERSON HOLDS, which is what a desk picks between. Before
  // 268 there was never more than one of these on screen, whatever the truth.
  const people = (rows || []).filter((r) => r.kind === 'person');

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
        margin: '0 0 16px', lineHeight: 1.7, maxWidth: 640,
      }}>
        When somebody verifies their identity while signed in as the wrong one of
        their own accounts, the ID attaches to the account they were standing in.
        This moves it to the one it belongs to. It cannot merge two people, and it
        does not close the account it moves the identity off — that account keeps
        working, and keeps its own Connect Code.
      </p>

      <Card>
        <Field
          label="OFX Connect Code"
          hint="The code the person reads to you off their own screen. Not their name — there is deliberately no way to search for one."
          htmlFor="reattach-lookup"
        >
          <input
            id="reattach-lookup"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            placeholder="OFX-7P8QY3V9C"
            autoComplete="off"
            spellCheck={false}
            style={{ ...textInputStyle(false), textTransform: 'uppercase' }}
          />
        </Field>

        <Field
          label="Why are you opening this record?"
          hint="Written to the append-only activity log under your name, before anything is read, and not editable afterwards."
          htmlFor="reattach-read-reason"
        >
          <textarea
            id="reattach-read-reason"
            rows={2}
            value={readReason}
            onChange={(e) => setReadReason(e.target.value)}
            placeholder="responding to ticket #4821"
            style={{ ...textInputStyle(false), resize: 'vertical' }}
          />
        </Field>

        {readTooShort && readReason.length > 0 && (
          <p style={{ margin: '-4px 0 12px', fontSize: 11.5, color: O.inkFaint }}>
            A few more words — at least {MIN_REASON} characters.
          </p>
        )}

        <PrimaryCTA
          onClick={open}
          busy={busy}
          disabled={busy || readTooShort || !lookup.trim()}
        >
          {busy ? 'Opening…' : 'Record the reason and open'}
        </PrimaryCTA>
      </Card>

      {error && <div style={{ marginTop: 14 }}><Notice tone="critical">{error}</Notice></div>}

      {busy && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner /><span style={{ fontSize: 12.5, color: O.inkMuted }}>Reading…</span>
        </div>
      )}

      {rows && rows.length === 0 && (
        <div style={{ marginTop: 14 }}>
          <Notice tone="warning">Nothing matches that code.</Notice>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="footprint">
          <Card>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 4px' }}>
              Everything this person touches
            </h3>
            <Meta>
              {people.length > 1
                ? `${people.length} profiles belong to this human. The identity is on the one marked below.`
                : 'One profile. If the identity is on the wrong account, the other one is not linked to this person yet — look it up by its own code.'}
            </Meta>

            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rows.map((r, i) => (
                <div
                  key={`${r.kind}-${r.reference || i}`}
                  style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
                >
                  <StatusPill tone="quiet">{FOOTPRINT_KINDS[r.kind] || r.kind}</StatusPill>
                  <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 700, color: O.ink }}>
                    {r.label}
                  </span>
                  {r.reference && (
                    <span style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted }}>
                      {r.reference}
                    </span>
                  )}
                  <span style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted }}>
                    {r.detail}
                  </span>
                  {r.status && <StatusPill tone="quiet">{r.status}</StatusPill>}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="move-panel">
          <Card>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: '0 0 4px' }}>
              Move the identity
            </h3>
            <Meta>
              Both fields take an OFX Connect Code, or a profile id if the account
              never got a code. The identity moves; the Connect Codes do not —
              nothing is reissued.
            </Meta>

            <div style={{ marginTop: 12 }}>
              <Field
                label="It is on this profile"
                hint="The account the ID attached to by mistake. It keeps working afterwards."
                htmlFor="reattach-from"
              >
                <input
                  id="reattach-from"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  placeholder="OFX-YUQ4J2P2X"
                  autoComplete="off"
                  spellCheck={false}
                  style={{ ...textInputStyle(false) }}
                />
              </Field>

              <Field
                label="It belongs on this one"
                hint="The account it should have been on."
                htmlFor="reattach-to"
              >
                <input
                  id="reattach-to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="OFX-F4MQTMEFG"
                  autoComplete="off"
                  spellCheck={false}
                  style={{ ...textInputStyle(false) }}
                />
              </Field>

              <Field
                label="Why are you moving it?"
                hint="A second reason, because this is a second act — reading a record and changing one are not the same disclosure."
                htmlFor="reattach-move-reason"
              >
                <textarea
                  id="reattach-move-reason"
                  rows={2}
                  value={moveReason}
                  onChange={(e) => setMoveReason(e.target.value)}
                  placeholder="verified on the wrong account, ticket #4821"
                  style={{ ...textInputStyle(false), resize: 'vertical' }}
                />
              </Field>

              {moveTooShort && moveReason.length > 0 && (
                <p style={{ margin: '-4px 0 12px', fontSize: 11.5, color: O.inkFaint }}>
                  A few more words — at least {MIN_REASON} characters.
                </p>
              )}

              {moveError && (
                <div style={{ marginBottom: 12 }}><Notice tone="critical">{moveError}</Notice></div>
              )}

              {moved && (
                <div style={{ marginBottom: 12 }}>
                  <Notice tone="info">
                    {moved.outcome === 'already moved'
                      ? 'Already done — the identity was on the second profile before you pressed this. Nothing changed.'
                      : 'Moved. The footprint above has been read again so you are looking at the record rather than at this screen’s opinion of it.'}
                  </Notice>
                </div>
              )}

              <PrimaryCTA
                onClick={move}
                busy={moving}
                disabled={moving || moveTooShort || !from.trim() || !to.trim()}
              >
                {moving ? 'Moving…' : 'Record the reason and move it'}
              </PrimaryCTA>

              {moved && (
                <div style={{ marginTop: 10, maxWidth: 220 }}>
                  <QuietButton onClick={() => {
                    setFrom(''); setTo(''); setMoveReason(''); setMoved(null);
                  }}>
                    Clear this
                  </QuietButton>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      <p style={{ marginTop: 14, fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint, maxWidth: 640 }}>
        {PERMISSION.permission_key} requires your authenticator and a reason on
        every use. It refuses to move an identity onto a profile that already
        carries a different one — merging two people is a different act, and not
        this one.
      </p>
    </div>
  );
}
